import { Listing } from 'models/Listing';
import { User } from 'models/User';
import { DataRoomMember } from './DataRoomMember';
import { DealRoom } from 'models/DealRoom';
import { DataRoomNode, DataRoomNodeTag } from './DataRoomNode';
import { Question } from './QAndA';
import { DataRoomShareableLink } from 'models/ShareableLink';
import { PresignedFile } from 'models/File';
import { BrandingColors, BrandingLogos, ObjectValues } from 'types';
import { SoftSignal } from 'models/Lead';

export type DataRoom = {
  _id: string;

  name: string;

  isSetup?: boolean;
  hasFaqs?: boolean;

  sessionTimeout: number;
  customWelcomeMessage: string;
  teams?: string[];
  questionSubjects: string[];
  existTags: DataRoomNodeTag[];
  buySideCanInviteColleagues?: boolean;

  listingId?: string;
  listing?: Partial<Listing> | null;

  dealRoomId?: string;
  dealRoom?: Partial<DealRoom>;

  userId: string;
  user: Partial<User> | null;

  currentMember: DataRoomMember;

  isClosed?: boolean;
  isDisabled?: boolean;
  isDisabledQA?: boolean;

  docIndexUpdatedAt?: Date;

  hasDataRoomAccess?: boolean;
  requestedDataRoomAccess?: boolean;

  accessConfirmation: boolean;
  accessConfirmationDays?: number;

  canRenewFileAccess?: boolean;
  renewFileAccessDays?: number;

  createdAt: Date;

  allowSoftSignals?: boolean;
  isSoftSignalsLocked?: boolean;

  isWhitelabeled?: boolean;

  isNativeEsignEnabled?: boolean;

  storageCapacityUtilization: number; //in bytes

  defaultsSettingsSetupAt?: Date;
  defaultsSettingsSetupBy?: { firstName: string; lastName?: string };

  defaultsSecuritySettingsSetupAt?: Date;
  defaultsSecuritySettingsSetupBy?: { firstName: string; lastName?: string };

  hasSecureLinkActivity: boolean;
} & DataRoomSecuritySettings &
  BrandingColors &
  BrandingLogos;

export type DataRoomSecuritySettings = {
  fileSecurity: FileSecurity;
  expirationDays?: number | null;
  blurProtection?: boolean;

  watermarkDiagonal: boolean;
  watermarkHeader: boolean;
  watermarkFooter: boolean;
  watermarkFullName: boolean;
  watermarkEmail: boolean;
  watermarkIpAddress: boolean;
};

export const FILE_SECURITY = {
  BASIC: 'basic',
  ADVANCED: 'advanced',
  MAXIMUM: 'maximum',
} as const;

export type FileSecurity = ObjectValues<typeof FILE_SECURITY>;

export type DataRoomInvitation = {
  dealRoom: DealRoom;
  dataRoomId: string;
  listing: Listing;
  createdAt: Date;
};

export type DataRoomMemberNodes = {
  dataRoomMember: DataRoomMember;
  dataRoomNodes: Array<DataRoomNode>;
};

export type UploadDataRoomNode = {
  presignedFile: PresignedFile;
  dataRoomNode: DataRoomNode;
  removedDataRoomNodeId?: string;
};

export type UploadedDataRoomNode = {
  dataRoomNode?: DataRoomNode;
  removedDataRoomNodeId?: string;
};

export type DataRoomSearchData = {
  dataRoomNodes: DataRoomNode[];
  dataRoomQuestions: Question[];
  dataRoomFaqs: Question[];
  dataRoomSecureLinks: DataRoomShareableLink[];
};

export type DataRoomSheet = {
  _id?: string;
  dataRoomName: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  colleagues?: string | string[];
} & Record<string, any>;

export type DataRoomSubroute =
  | 'dashboard'
  | 'members'
  | 'questions/questions-and-answers'
  | 'questions/frequently-asked-questions'
  | 'reports'
  | 'reports/overview'
  | 'reports/activity-log'
  | 'settings'
  | 'file-security'
  | 'documents'
  | 'rfi/list'
  | 'rfi/create'
  | 'rfi/create-template'
  | 'rfi/edit-template'
  | 'shareable-links'
  | 'waiting-room'
  | 'ndas'
  | 'ndas/new';

export type AutomationResponseBody = {
  softSignal?: SoftSignal;
  withdrawInterest?: boolean;
  answerId?: string;
};
